import { registrationMissingLabels } from '@/libs/compliance'
import { formatDateTime, formatTime } from '@/libs/dayjs'
import i18n from '@/libs/i18n'
import { formatAmount } from '@/libs/money'
import { formatQty } from '@/libs/quantity'
import type { IRequisitionNotApproved } from '@/types/MaterialsRequisition'
import type { IExpiredSupplierPrices } from '@/types/StockCatalogue'
import type { IStockInvoiceBalance, IStockInvoiceMismatch } from '@/types/StockInvoice'
import type { IReceiptMismatch } from '@/types/StockReceiving'

export enum Errors {
    InternalServerError = 1,
    InvalidData         = 2,
    NotFound            = 3,
    Unauthorized        = 4,
    FailedToInsert      = 5,
    ItemAlreadyExists   = 6,
    Forbidden           = 7,
    IncorrectStatus     = 8,
    /**
     * The retail counter refusing: a PIN that matches nobody, a PIN belonging to
     * somebody who was not given THAT counter, or a counter session that ran out.
     *
     * It arrives on a 401 like Unauthorized does and is deliberately NOT the same
     * thing: the portal session is untouched, and only the counter is locked. The
     * fetch store keys on this to leave the portal alone.
     */
    TillLocked          = 9,
    /**
     * An invoice that cannot say who issued it: the account has neither a personal
     * tax number nor a foreign-operator declaration. ZDavPR requires one of the two,
     * and there is no fallback to the hotel's number — so the issue is refused before
     * anything is written, and the screen says where the missing detail is set.
     */
    OperatorNotIdentified = 10,
    /**
     * The same press of the till's finish button is already being handled — or was
     * interrupted halfway. Never taken over automatically: the interrupted attempt
     * may already have fiscalised an invoice and moved stock.
     */
    OperationInProgress   = 11,
    /** One attempt name arriving with two different baskets: the till is confused. */
    OperationMismatch     = 12,
    /**
     * A counter sale of something the batches do not cover (docs/MATERIALNO.md §9c).
     * Its own code because the till ACTS on it — it asks a manager for a reason and
     * sends the same basket again — rather than showing it and giving up.
     */
    StockNotCovered       = 13,
    /**
     * A guest write refused because the hotel already has that person: the surname,
     * the given name and the date of birth match another profile.
     *
     * Its own code beside ItemAlreadyExists because the desk ACTS on it. The refusal
     * carries the existing profile in `details` — see IGuestConflict — and the screen
     * offers to open that record, or to put this booking on it, instead of leaving a
     * receptionist with "the save failed" and a guest standing at the counter.
     */
    GuestAlreadyExists    = 15,
    /**
     * A route of a module this hotel has not bought (docs/MODULI.md §3.2). It rides
     * on 403 exactly like Forbidden, and the code is the only thing that tells the
     * two apart — which matters, because the ways out are opposite: "you have no
     * right" is a conversation with whoever keeps the roles, and "this house has no
     * module" is one with whoever signs the contract. Telling somebody they lack a
     * permission when the hotel lacks the module sends them to the wrong person and
     * they come back none the wiser.
     */
    ModuleNotLicensed     = 19,
    /**
     * The two ways a turn of the assistant ends with nobody having refused anything
     * (docs/AI.md §31): it used its whole budget, or the process working it out went
     * away mid-answer — a deploy, a restart.
     *
     * Their own codes because the portal ACTS differently on each. One says „vprašaj
     * ožje“, the other says „vprašaj znova, prekinilo se je“, and the generic code would
     * print "something went wrong" over both — which is the sentence that teaches people
     * to reload, and reloading used to be how an answer was thrown away.
     */
    AssistantTurnTimedOut = 95,
    AssistantTurnLost     = 96,
    /**
     * A stamp, a correction or an absence for a day this person has no work period
     * covering (docs/OSEBJE.md §4).
     *
     * It shares 403 with Forbidden and means something else: nothing about the caller's
     * rights is wrong, and no right anybody could be given would help — the personnel
     * card is what is missing. The two have opposite ways out, which is the same reason
     * ModuleNotLicensed above has its own code, so the sentence has to name the card.
     */
    StaffNotEmployed      = 39,
    /**
     * A group block naming more rooms of a type than the property has free across
     * the whole stay (Logic.assertGroupBlocksFit). 409, and its own code so the
     * screen can read room_type_id/requested/available/worst_night out of `details`
     * and say "na voljo le 3" rather than a bare refusal.
     */
    GroupBlockExceedsAvailability = 23,
    /**
     * Held rooms given back from inside the rooming review AFTER the group's release
     * date, or where the cancellation ladder keeps anything (docs/SKUPINE.md §16).
     *
     * Its own code because it is not a refusal at all — it is a PRICE. The request is
     * well formed and the house is willing; what is missing is the desk agreeing to the
     * figure, so the body carries the ladder's quote per block and in total in `details`
     * and the panel draws it with a Confirm beside it. IncorrectStatus would print
     * „napačno stanje“ over „600 €“, which is the one thing this must never do.
     */
    GroupShrinkFeeUnconfirmed = 102,
    /**
     * A user account named on a facilities resource that already has one
     * (Logic.assertResourceAccount). 409, carrying which resource in `details`.
     */
    FacilityResourceAccountBound = 24,
    /**
     * A room a stay or a group's held block already occupies, refusing a report,
     * a close, or a move (Logic.resolveRoomHolder). 409, carrying who — a
     * reservation or a group, by kind, id, name and dates — in `details.held_by`.
     */
    RoomBlocked = 25,
    /**
     * A group's held room dragged onto a room of a DIFFERENT type than the block
     * it is held under (Logic.MoveGroupHeldRoom). 409, carrying the group's name,
     * the room and the two types in `details` — the chessboard reads it to ask
     * "soba drugega tipa — blok skupine se prilagodi?" before it happens, rather
     * than after.
     */
    GroupBlockTypeChange = 26,
    /**
     * A folio line refused because another LIVE line already carries the same
     * document reference — the "one live line per document reference" index saying
     * so (migration 134).
     *
     * Its own code because of what it replaced: a folio split of a spa booking
     * answered FailedToInsert with a 400, and "failed to insert" is true of every
     * failed write there has ever been. This one is a fact about the world — that
     * document is already posted, live, somewhere on this property — and the desk
     * can do something about it.
     */
    ChargeReferenceInUse = 31,
    /**
     * A room somebody marked for cleaning that is out of order or out of service.
     *
     * It shares 409 with IncorrectStatus and says something the desk can act on that
     * "wrong state" does not: the room is closed, and the way in is to reopen it.
     */
    RoomOutOfService = 32,
    /**
     * A room marked for cleaning that is already on today's round.
     *
     * Barely a refusal — what the person asked for is already true — so the body carries
     * the task in `details` and the screen shows the work rather than a no.
     */
    HousekeepingTaskAlreadyOpen = 33,
    /**
     * An accommodation line refused because the stay already has a live line for that
     * night — the "one live line per night" index saying so (migration 140).
     *
     * Its own code because it is the OTHER unique index over the same collection, and
     * every duplicate used to be reported as ChargeReferenceInUse: a split of the
     * accommodation line was refused with "this document is already posted" about a
     * line that carries no document reference at all.
     */
    RoomChargeAlreadyPosted = 36,
    /**
     * Settled money being moved between the cards of one booking, in an amount the
     * source card does not hold of that payment.
     *
     * Its own code because the desk ACTS on it: the answer is a smaller amount or
     * another payment, and both are one field away on the window that was refused.
     * InvalidData would say the request was malformed, which it is not.
     */
    PaymentTransferNotCovered = 37,
    /**
     * A folio that would make a document with no lines: an empty card, or one whose
     * every line has been cancelled by a storno that reverses it in full — and those
     * pairs are not printed (docs/BLAGAJNA.md § Kaj gre na račun).
     *
     * Its own code because of what it replaced: the empty card answered InvalidData,
     * which tells a receptionist her screen sent something malformed about a request
     * that was perfectly well formed, and said nothing at all about the second case,
     * where the card is FULL and the invoice would still be blank. The way out is on
     * the card, and the sentence has to send her there.
     */
    FolioHasNothingToInvoice = 47,
    /**
     * A receipt recorded against a pro forma that cannot take one: cancelled,
     * superseded, expired, or already paid in full.
     *
     * Its own code beside IncorrectStatus because the desk ACTS on it, and the four
     * ways on are different papers: a dead pro forma is reissued, and a paid one
     * already has the advance invoice somebody is looking for. A bare wrong-state
     * refusal names neither.
     */
    ProformaNotPayable = 48,
    /**
     * A receipt larger than what the pro forma still asks for.
     *
     * Its own code because the refusal carries the figure that would be accepted —
     * `details.remaining` — and the dialog offers it, which is one field away.
     * InvalidData would tell a receptionist her screen sent something malformed
     * about a perfectly well formed request for money that is simply more than was
     * asked for.
     */
    ProformaAmountExceeded = 49,
    /**
     * A prepayment being deducted or credited that a live final invoice has already
     * taken. Its own code so the screen can NAME the invoice holding it — the way out
     * is to credit that invoice first, not to press the button again — which is why
     * the refusal carries `{invoice_id, invoice_number}` in its details.
     */
    AdvanceAlreadyConsumed = 50,
    /**
     * A final invoice whose deductible prepayments come to more than the stay itself.
     * Its own code because it is not an error in the request at all: the guest has
     * overpaid, and the answer is a partial credit note against the advance invoice
     * plus a refund — a different button on a different dialog. A silent negative
     * invoice would be refused by the POS anyway, with a message about a bill nobody
     * would connect to a deposit.
     */
    AdvanceExceedsInvoiceTotal = 51,

    /**
     * A department dropped onto itself, onto one of its own descendants, or onto a
     * chain already too deep (docs/OSEBJE.md §17).
     *
     * It is listed rather than handled: the API's sentence NAMES the department in the
     * way ("«Vodstvo» already sits under it…"), which is the one fact somebody needs
     * to pick a different parent, and `parseError`'s `default` branch already prints
     * the server's own words. A house sentence here would say less than the server's.
     */
    StaffDepartmentCycle = 57,

    /**
     * Closing a month of the working-time record (docs/EVIDENCA-CASA.md
     * § „Zaključevanje meseca“). Four codes, and three of them are refusals of the
     * close itself: the month has not ended, something inside it is still waiting to be
     * decided, it is already closed.
     *
     * All four are LISTED rather than handled, for StaffDepartmentCycle's reason: the
     * API's sentence carries the one next step in each case — wait until the 1st, decide
     * the three corrections, reopen it first — and `parseError`'s default branch already
     * prints the server's own words. A house sentence here would say less.
     *
     * StaffTimeMonthHasPending additionally carries `{corrections, absences}` in its
     * details, which is what lets a screen send somebody to the right queue rather than
     * to both.
     */
    StaffTimeMonthNotEnded = 67,
    StaffTimeMonthHasPending = 68,
    StaffTimeMonthAlreadyClosed = 69,
    /**
     * A write into a month that is closed: approving a correction, entering or approving
     * an absence, stamping a past day inside it. It is the one of the four that the
     * people who cannot reopen a month actually meet, which is why the server's sentence
     * names the way out — „mesec je zaključen, najprej ga ponovno odprite“ — and why the
     * dialogs simply print it.
     */
    StaffTimeMonthClosed = 64,

    /**
     * The Event Manager's own refusals (docs/DOGODKI.md §2.11). Each carries the facts
     * a person acts on in `details`: which hall, when, held by whom; which part's
     * guarantee; what does not add up. The sentences below are built from those facts
     * in the desk's language and its clock — the server's own line is English and in
     * UTC, and a receptionist reading "konferencna at 06:00" would go looking for a
     * six o'clock booking that starts at eight.
     */
    EventVenueUnavailable  = 97,
    EventVersionSuperseded = 98,
    EventGuaranteeLocked   = 99,
    EventSetupOverlap      = 100,
    EventLinesUnbalanced   = 101,
    EventNotSettled        = 111,

    /**
     * The three refusals of a supplier's invoice (docs/MATERIALNO-RACUNI.md, WP-M2).
     *
     * All three carry facts in `details` rather than only a sentence, because each is a
     * refusal a book-keeper ACTS on: 103 names the lines, what the other paper said and
     * the limit each went past; 105 names both totals. 104 carries nothing and needs
     * nothing — the screen reloads the invoice and shows the match it already has.
     */
    SupplierInvoiceOutOfTolerance = 103,
    SupplierInvoiceAlreadyMatched = 104,
    SupplierInvoiceNotBalanced    = 105,

    /**
     * A catalogue price whose validity has RUN OUT being put on an order
     * (docs/MATERIALNO-KATALOG.md §4, WP-M3a).
     *
     * It carries every lapsed line in `details` — the article, the supplier and the day
     * the price stopped being true — and it is the one refusal in this module a screen
     * is MEANT to be able to send the same request past: with `accept_expired_price` and
     * a reason, which both go into the revision trail. A sentence alone would leave that
     * door with nothing to open it.
     */
    SupplierPriceExpired = 106,

    /**
     * A STRICT posting of a delivery further from its order than the house allows
     * (docs/MATERIALNO-PREVZEM.md §4, WP-M3c).
     *
     * It is the only refusal in receiving, and only a desk that asked for it ever meets
     * it: the ordinary posting quarantines the line and opens an issue instead. The
     * details name the row, both quantities and the limit, because a storekeeper is
     * standing in front of a driver and „ne gre“ without a pallet number is a refusal
     * they cannot act on.
     */
    ReceiptOutOfTolerance = 107,

    /**
     * A purchase the house's own ladder has not signed off yet
     * (docs/MATERIALNO-ZAHTEVKI.md, WP-M4).
     *
     * It carries the requisition's number, the rung it is standing on, the role whose
     * turn it is and the hour that rung runs out — because the remedy is a person to
     * ask, and „ni odobreno“ without a name is a refusal a buyer cannot act on. With
     * `level` zero there is no ladder at all: the purchase was big enough to need a
     * requisition and nobody wrote one, which is a different errand entirely.
     */
    RequisitionNotApproved = 108,

    /**
     * A transfer between two houses that are not in the same chain (WP-M5,
     * `docs/VERIGE-ZASLONI.md`).
     *
     * Its own code beside InvalidData because the remedy is a REGISTER and not a field:
     * nothing on the form is wrong, and no retyping helps — somebody with
     * `chains:manage` has to put both houses in one chain. The details carry the two
     * property ids, which is what a support desk reads and never what the sentence says:
     * a receptionist meeting this refusal is told what is possible, not shown two
     * ObjectIDs.
     */
    TransferOutsideChain = 109,

    /**
     * An article asked to be counted MORE COARSELY than one of its own figures allows
     * (docs/MATERIALNO.md §9g).
     *
     * Its own code beside InvalidData because the remedy is a LEDGER and not a field:
     * the form is right and the article is in an ordinary state, and what is in the way
     * is a quantity already written down — 0,255 kg on some movement — that two decimal
     * places cannot say. The details name the collection it sits in, so the sentence can
     * send somebody to the right register rather than through the whole house.
     */
    StockDecimalsWouldRound = 112,

    /**
     * An article RESCALED while somebody was typing a quantity of it
     * (docs/MATERIALNO.md §9g).
     *
     * Every quantity-carrying request may name the scale the form was prepared at —
     * `expected_decimals` — and the service refuses the whole request where the article
     * no longer counts that way. Without it the figure would be booked at the NEW scale:
     * 0,25 l typed against a hundredths article and stored as 25 arrives at an article
     * now counted in whole litres as twenty-five litres of cleaner.
     *
     * Its own code beside StockDecimalsWouldRound, which is the opposite errand: that one
     * refuses the RESCALE because a figure already written down cannot survive it, and
     * this one refuses a FIGURE because the rescale already happened. The remedy here is
     * a form reloaded and a quantity typed again, which is why every screen that sends a
     * quantity reloads its articles on it rather than only printing a sentence.
     */
    StockScaleChanged = 113,

    /**
     * A guest eTurizem cannot be told about yet: the stored PROFILE the filing would
     * freeze is short of a field the state requires (docs/ETURIZEM.md §2).
     *
     * Its own code beside InvalidData because the remedy is another SCREEN and not a
     * field on this one: nothing that gets reported comes out of a form at all — it is
     * read off the guest's record — so „napačni podatki“ over a press of „Prijavi“ tells
     * the desk to correct something it never typed. The details name the fields, which
     * is what lets the unregistered panel offer one button that opens the guest and
     * another that files them.
     */
    GuestRegistrationIncomplete = 115,

    /**
     * The three answers about a partner facility — a spa or a golf club run by another
     * company in Rezervacije (docs/PARTNERSKE-ZMOGLJIVOSTI.md): this deployment cannot
     * reach one at all, the partner did not answer, the partner answered no. The last
     * carries the partner's own sentence after a colon, which is the part the desk needs
     * — "that time is no longer available" is the partner's to say.
     */
    PartnerNotConfigured = 137,
    PartnerUnavailable   = 138,
    PartnerRefused       = 139,
}

/**
 * Code 112's facts (`StockDecimalsWouldRound`): the two scales, and the collection
 * holding the first figure the coarser one could not express.
 */
interface IStockDecimalsRounding {
    collection?: string
    from?:       number
    to?:         number
}

interface IEventVenueHold {
    facility_name?: string
    at?: string
    held_by?: string
    reason?: 'not_published' | 'closed' | 'held' | ''
    diary_from?: string
    diary_to?: string
    work_from?: string
    work_to?: string
    held_from?: string
    held_to?: string
    preparation_minutes?: number
    teardown_minutes?: number
}

/**
 * The refusal's own facts, for a screen that has to DO something rather than print a
 * different sentence. Null when the body carries none, which is every refusal except
 * the few that carry an object somebody presses.
 */
export const parseErrorDetails = <T>(errorObject: unknown): T | null =>
    (errorObject as { error?: { details?: T } })?.error?.details ?? null

/**
 * The API's own code, for a caller that has to DO something different rather than
 * print a different sentence. Zero when the body carries none.
 */
export const parseErrorCode = (errorObject: unknown): number =>
    (errorObject as { error?: { code?: number } })?.error?.code ?? 0

/**
 * Whether a refusal is the rescale guard's (`StockScaleChanged`).
 *
 * It takes the STATUS as well as the body, because the guard reached the portal before
 * the service had taken its number: the code decides it wherever one arrives, and a 409
 * carrying a code this file has never heard of is read as the same refusal rather than
 * as nothing. That fallback is deliberately narrow — every OTHER 409 this module answers
 * (a month closed, a paper already posted, a scale that would round) is a code listed in
 * `Errors`, so it is recognised and left alone.
 *
 * A caller acts on `true` by reloading the articles its form was built from, and prints
 * `parseError(body)` either way — which is the server's own sentence for a code nobody
 * here knows yet, and the house sentence for the one we do.
 */
export const isStockScaleChanged = (status: number | null | undefined, body: unknown): boolean => {
    if (status !== 409) return false

    const code = parseErrorCode(body)

    if (code === Errors.StockScaleChanged) return true

    // A numeric enum answers the NAME for a code it holds and nothing for one it does
    // not, which is what "a code this file has never heard of" means in TypeScript.
    return !!code && Errors[code] === undefined
}

/**
 * The server's own sentence, verbatim. Empty when the body carries none.
 *
 * parseError() now prefers this too for every code without its own translated
 * sentence (see its own comment) — this export remains for a caller that wants the
 * raw text unconditionally, bypassing even the handful of codes parseError()
 * deliberately overrides with a translated one (ModuleNotLicensed and the rest).
 */
export const serverMessage = (errorObject: unknown): string =>
    (errorObject as { error?: { message?: string } })?.error?.message ?? ''

/**
 * Turns the API's {"error": {"code": n, "message": …}} body into a message for the
 * desk. Reads through i18n.global so it works outside a setup context too.
 *
 * # The server's own words win by default
 *
 * Found live, 2026-09-03: reporting a blocking fault on an occupied room answered
 * 409 with a precise reason ("the room is booked on N nights… move the stay or
 * shorten the block"), and the toast showed "Napaka pri urejanju" — the desk was
 * told THAT it failed and never WHY, for a refusal whose whole point was the why.
 * ItemAlreadyExists and IncorrectStatus used to answer the caller's own fallback
 * (or the generic sentence) and never look at body.message at all; every OTHER
 * unlisted code fell through the same door via `default`.
 *
 * Both now read body.message FIRST — a caller's fallback is still used, but only
 * when the server sent nothing to show, and the generic sentence is the true last
 * resort it was always meant to be. The codes below that return a fixed
 * TRANSLATED sentence regardless of the fallback (ModuleNotLicensed,
 * OperatorNotIdentified, OperationInProgress, OperationMismatch,
 * GuestAlreadyExists) are UNCHANGED and deliberately so — each has its own comment
 * explaining why a screen-specific SL/EN sentence beats the server's raw English
 * one for exactly that refusal; this fix is about the codes that had no such
 * sentence and were showing NOTHING useful instead.
 */
export const parseError = (errorObject: unknown, fallback = ''): string => {
    const t = i18n.global.t
    const te = i18n.global.te
    const raw = (errorObject as { error?: unknown })?.error

    // A bare-string body (PrintHTTP.WriteErrorMessage: {"error": "..."}) carries no
    // code for anything below to key on, so every refusal shaped this way used to
    // fall straight through to the generic sentence — exactly the ones with no
    // OTHER way to reach the screen, since there is no details object either.
    // Found live 2026-09-03: a decode failure answered a 400 this way, and the
    // toast said "could not save" while the API log held the actual reason.
    if (typeof raw === 'string') return raw || fallback || t('errors.generic_error')

    const body = raw as { code?: number, message?: string } | undefined
    const code = body?.code

    if (!code) return fallback || t('errors.generic_error')

    // The API names the refused field after the generic prefix — "invalid data:
    // fiscal_tax_number" — so the desk can be told which field, in its own language.
    const field = body?.message?.match(/^invalid data: ([a-z_]+)$/)?.[1]

    if (code === Errors.InvalidData && field) {
        const key = `errors.fields.${field}`

        return te(key) ? `${t('errors.invalid_data')}: ${t(key)}` : t('errors.invalid_data')
    }

    // A refusal that carries its own sentence beyond the bare "invalid data" is a
    // refusal somebody wrote for a person to read ("the group names no company …"),
    // and swallowing it left the desk with two words and no way forward.
    const sentence = body?.message?.replace(/^invalid data:?\s*/, '') ?? ''

    switch (code) {
        case Errors.InvalidData:       return sentence ? `${t('errors.invalid_data')}: ${sentence}` : t('errors.invalid_data')
        case Errors.NotFound:          return t('errors.not_found')
        case Errors.Unauthorized:      return t('errors.unauthorized')
        case Errors.Forbidden:         return t('errors.unauthorized')
        // Always its own words, never the caller's fallback and never the generic
        // refusal: nothing is wrong with the account, and the screen behind it is not
        // one this hotel has.
        case Errors.ModuleNotLicensed: return t('errors.module_not_licensed')
        case Errors.ItemAlreadyExists: return body?.message || fallback || t('errors.conflict')
        case Errors.IncorrectStatus:   return body?.message || fallback || t('errors.generic_error')
        // Always its own words, never the caller's fallback: "the sale failed" is
        // exactly the wrong thing to tell somebody whose account is one field short
        // of being able to sell.
        case Errors.OperatorNotIdentified: return t('errors.operator_not_identified')
        case Errors.OperationInProgress:  return t('errors.operation_in_progress')
        case Errors.OperationMismatch:    return t('errors.operation_mismatch')
        // The server's own sentence: it names the articles and by how much they are
        // short, which is precisely what the person at the counter needs.
        case Errors.StockNotCovered:      return body?.message ?? t('errors.generic_error')
        // Always its own words, never the caller's fallback: "the guest could not be
        // saved" is exactly the wrong thing to tell somebody whose only problem is
        // that the hotel already has the person in front of them. The screens that
        // can do something about it read the details and show the profile itself;
        // this sentence is for the ones that only print a line.
        case Errors.GuestAlreadyExists:   return t('errors.guest_already_exists')
        // Always its own words: the server's sentence is right and it is in English,
        // and this one is read at a counter with a guest waiting. Nothing is broken —
        // the document is simply already on a card.
        case Errors.ChargeReferenceInUse: return t('errors.charge_reference_in_use')
        // The night, not a document. Its own sentence for the same reason: the
        // server's is right and in English, and this one is read with a guest waiting.
        case Errors.RoomChargeAlreadyPosted: return t('errors.room_charge_already_posted')
        // Its own words for the same reason: the server's sentence is right and in
        // English, and this one is read at a counter with a guest waiting. Nothing is
        // broken — the card simply does not hold that much.
        case Errors.PaymentTransferNotCovered: return t('payment.transfer_not_covered')
        // Its own words for the same reason: the server's sentence is right and in
        // English, and this one is read at a counter with a guest waiting. It also has
        // to say the second half — that a card can be full and still have nothing to
        // invoice — which no English sentence about "no lines" conveys to a
        // receptionist looking at five lines on her screen.
        case Errors.FolioHasNothingToInvoice:  return t('invoice.folio_nothing_to_invoice')
        // The one refusal in this list that is about a SECOND document: the prepayment
        // is spent, and the sentence is useless without saying which invoice spent it.
        // The number comes out of the details rather than out of the server's English
        // sentence, so the desk is told where to go and not merely that it may not.
        case Errors.AdvanceAlreadyConsumed: {
            const consumed = parseErrorDetails<{ invoice_number?: string }>(errorObject)

            return consumed?.invoice_number
                ? t('invoice.advance_already_consumed', { number: consumed.invoice_number })
                : t('invoice.advance_already_consumed_unknown')
        }
        // Its own words because nothing is broken and nothing the desk typed was wrong:
        // the guest has paid more in advance than the stay came to, and the sentence
        // has to send somebody to the credit note rather than apologise for a failure.
        case Errors.EventVenueUnavailable: {
            const hold = parseErrorDetails<IEventVenueHold>(errorObject)

            const venue = hold?.facility_name || '?'
            const at    = formatDateTime(hold?.at)

            // Three refusals with nobody to name, and „zasedena: ?“ is wrong about all of
            // them: a hall with no terms at that hour is not taken, and neither is one
            // the house has shut.
            if (hold?.reason === 'not_published') {
                // What the diary DOES hold that day is the other half of the answer: a day
                // with terms until 20:00 wants its hours extended, a day with none wants
                // the weekday opened or the diary published.
                const known = (value?: string): boolean => !!value && !value.startsWith('0001-')

                return known(hold.diary_from) && known(hold.diary_to)
                    ? t('event.errors.venue_not_open_range', { venue, at, from: formatTime(hold.diary_from), to: formatTime(hold.diary_to) })
                    : t('event.errors.venue_not_open_day', { venue, at })
            }
            if (hold?.reason === 'closed')        return t('event.errors.venue_closed', { venue, at })
            if (!hold?.held_by)                   return t('event.errors.venue_taken_unnamed', { venue, at })

            return t('event.errors.venue_taken', { venue, at, by: hold.held_by })
        }
        case Errors.EventSetupOverlap: {
            const hold = parseErrorDetails<IEventVenueHold>(errorObject)

            return t('event.errors.setup_overlap', {
                venue:       hold?.facility_name || '?',
                by:          hold?.held_by || '?',
                held:        `${formatTime(hold?.held_from)}–${formatTime(hold?.held_to)}`,
                preparation: hold?.preparation_minutes ?? 0,
                teardown:    hold?.teardown_minutes ?? 0,
            })
        }
        case Errors.EventGuaranteeLocked: {
            const lock = parseErrorDetails<{ part_title?: string, guaranteed?: number, requested?: number, past_cutoff?: boolean, cutoff_at?: string }>(errorObject)

            return t(lock?.past_cutoff ? 'event.errors.guarantee_past_cutoff' : 'event.errors.guarantee_locked', {
                part:       lock?.part_title || '?',
                guaranteed: lock?.guaranteed ?? 0,
                requested:  lock?.requested ?? 0,
                cutoff:     formatDateTime(lock?.cutoff_at),
            })
        }
        case Errors.EventVersionSuperseded: {
            const seen = parseErrorDetails<{ seen_version?: number, standing_version?: number }>(errorObject)

            return t('event.errors.version_superseded', { seen: seen?.seen_version ?? 0, standing: seen?.standing_version ?? 0 })
        }
        case Errors.EventLinesUnbalanced: {
            const balance = parseErrorDetails<{ difference?: number, faults?: { part_title?: string, kind?: string }[] }>(errorObject)

            return t('event.errors.lines_unbalanced', {
                difference: formatAmount(balance?.difference ?? 0),
                parts:      (balance?.faults ?? []).map(fault => fault.part_title).filter(Boolean).join(', ') || '—',
            })
        }
        // Always its own words, and the words are the FIELDS. The server's sentence says
        // only that something is missing, which is the refusal this code was given a name
        // to stop being: the desk has to be sent to a box, not told to go and look.
        case Errors.GuestRegistrationIncomplete: {
            const short = parseErrorDetails<{ missing?: string[] }>(errorObject)

            return t('registration.file_incomplete', { fields: registrationMissingLabels(short?.missing) })
        }
        case Errors.EventNotSettled: {
            const open = parseErrorDetails<{ balance?: number, unposted_lines?: number }>(errorObject)

            return t('event.errors.not_settled', { balance: formatAmount(open?.balance ?? 0), unposted: open?.unposted_lines ?? 0 })
        }
        // The three-way match refusing (docs/MATERIALNO-RACUNI.md). The sentence names
        // the FIRST line in full and says how many others there are, because the
        // remedy is a telephone call about one article — and the table on the detail
        // screen redraws with every fault on it, so nothing is lost by not listing
        // them all in a toast.
        case Errors.SupplierInvoiceOutOfTolerance: {
            const mismatch = parseErrorDetails<IStockInvoiceMismatch>(errorObject)
            const breaches = mismatch?.breaches ?? []
            const first = breaches[0]

            if (!first) return t('materialsInvoice.errors.out_of_tolerance_plain')

            const money = first.kind !== 'quantity'

            return t(breaches.length > 1 ? 'materialsInvoice.errors.out_of_tolerance_more' : 'materialsInvoice.errors.out_of_tolerance', {
                line:     first.line_index ?? 0,
                item:     first.item_name || '?',
                kind:     t(`materialsInvoice.tolerance_${first.kind}`),
                variance: money ? formatAmount(first.variance ?? 0) : formatQty(first.variance ?? 0, first.decimals),
                limit:    money ? formatAmount(first.limit ?? 0) : formatQty(first.limit ?? 0, first.decimals),
                more:     breaches.length - 1,
            })
        }
        case Errors.SupplierInvoiceAlreadyMatched: return t('materialsInvoice.errors.already_matched')
        // Both figures, because one of them alone is not a fact about anything — and
        // the two ordinary causes (a mistyped line, a delivery charge on no line) are
        // told apart by the size and the sign of the difference.
        case Errors.SupplierInvoiceNotBalanced: {
            const balance = parseErrorDetails<IStockInvoiceBalance>(errorObject)

            return t('materialsInvoice.errors.not_balanced', {
                stated:  formatAmount(balance?.stated_gross ?? 0, balance?.currency || 'EUR'),
                derived: formatAmount(balance?.derived_gross ?? 0, balance?.currency || 'EUR'),
            })
        }
        // A catalogue price that has run out being put on an order
        // (docs/MATERIALNO-KATALOG.md §4). The sentence names the FIRST lapsed line in
        // full and says how many others there are, for the reason 103's does: the
        // remedy is a telephone call about one article, and the catalogue screen paints
        // every lapsed row amber anyway, so nothing is lost by not listing them all in
        // a toast.
        case Errors.SupplierPriceExpired: {
            const lapsed = parseErrorDetails<IExpiredSupplierPrices>(errorObject)
            const prices = lapsed?.prices ?? []
            const first = prices[0]

            if (!first) return t('materials_catalogue.errors.price_expired_plain')

            return t(prices.length > 1 ? 'materials_catalogue.errors.price_expired_more' : 'materials_catalogue.errors.price_expired', {
                line:     first.line_index ?? 0,
                item:     first.item_name || '?',
                supplier: first.supplier_name || '?',
                to:       first.price_to || '?',
                more:     prices.length - 1,
            })
        }
        // The line, both quantities and the limit. A quantity is a COUNT and is printed
        // as one; a price breach is cents per unit and goes through formatAmount — the
        // same split the invoice's own refusal makes one case above.
        case Errors.ReceiptOutOfTolerance: {
            const mismatch = parseErrorDetails<IReceiptMismatch>(errorObject)
            const breaches = mismatch?.breaches ?? []
            const first    = breaches[0]

            if (!first) return t('receiving.errors.out_of_tolerance_plain')

            const money = first.kind === 'price'

            return t(breaches.length > 1 ? 'receiving.errors.out_of_tolerance_more' : 'receiving.errors.out_of_tolerance', {
                line:     first.line_index ?? 0,
                item:     first.item_name || '?',
                ordered:  money ? formatAmount(first.ordered ?? 0) : formatQty(first.ordered ?? 0, first.decimals),
                received: money ? formatAmount(first.received ?? 0) : formatQty(first.received ?? 0, first.decimals),
                limit:    money ? formatAmount(first.limit ?? 0) : formatQty(first.limit ?? 0, first.decimals),
                more:     breaches.length - 1,
            })
        }
        // The ask behind the order, and whose desk it is on. The zero level is the
        // other errand — no requisition exists — and it gets its own sentence rather
        // than one naming a role of "?" and a deadline of nothing.
        case Errors.RequisitionNotApproved: {
            const waiting = parseErrorDetails<IRequisitionNotApproved>(errorObject)

            if (!waiting?.level) return t('materialsRequisition.errors.required')

            return t('materialsRequisition.errors.not_approved', {
                number: waiting.requisition_number || '?',
                role:   waiting.approver_role || '?',
                level:  waiting.level,
                due:    waiting.due_at ? formatDateTime(waiting.due_at) : '—',
            })
        }
        // The rule and not the pair. The two ids ARE in `details` (ITransferOutsideChain)
        // and are deliberately not printed: they name nothing a person at the desk can
        // read, and the one sentence that helps is what a transfer is actually for.
        case Errors.TransferOutsideChain:      return t('materialsTransfer.errors.outside_chain')
        // The two scales and WHERE the figure that refuses them is. Without the place
        // the sentence is "something does not divide", which is a person reading a
        // house's whole ledger by hand.
        case Errors.StockDecimalsWouldRound: {
            const rounding = parseErrorDetails<IStockDecimalsRounding>(errorObject)

            return t('stock.errors.decimals_would_round', {
                from:  rounding?.from ?? 0,
                to:    rounding?.to ?? 0,
                where: rounding?.collection || '—',
            })
        }
        // Its own words and never the server's: the English sentence names the article
        // and the two scales, and what the person in front of the form has to be told is
        // the one thing it does not say — that the figure on the screen is now about a
        // different measure and has to be typed again. The screens reload their articles
        // on this code, so by the time the toast is read the form already can be.
        case Errors.StockScaleChanged:          return t('stock.errors.scale_changed')
        case Errors.AdvanceExceedsInvoiceTotal: return t('invoice.advance_exceeds_total')
        // Named as what it is — the card and not the person. The server's sentence is
        // right and in English, and "you have no right" would be a conversation with
        // whoever keeps the roles when the fix is a personnel card somebody has to write.
        case Errors.StaffNotEmployed:          return t('errors.staff_not_employed')
        // The four dead ends of a pro forma in one sentence, in the desk's language:
        // the server's is right and in English, and this one is read with somebody's
        // bank statement open. The screens that can do something about it check the
        // status before they offer the button at all.
        case Errors.ProformaNotPayable:        return t('proforma.not_payable')
        // Its own words AND the figure behind them. The refusal's whole point is the
        // amount that WOULD be accepted, and a sentence that omitted it would send a
        // receptionist back to the pro forma to work out a subtraction the server has
        // already done.
        case Errors.ProformaAmountExceeded: {
            const remaining = parseErrorDetails<{ remaining?: number }>(errorObject)?.remaining ?? 0

            return t('proforma.amount_exceeded', { remaining: formatAmount(remaining) })
        }
        case Errors.PartnerNotConfigured: return t('errors.partner_not_configured')
        case Errors.PartnerUnavailable:   return t('errors.partner_unavailable')
        case Errors.PartnerRefused: {
            const reason = (body?.message ?? '').replace(/^the partner facility refused:?\s*/, '')

            return reason ? t('errors.partner_refused', { reason }) : t('errors.partner_refused_plain')
        }
        // Every OTHER code — GroupBlockExceedsAvailability, FacilityResourceAccountBound
        // and whatever gets added next — is the general case this function exists
        // for: show what the server actually said, and only fall back when it said
        // nothing.
        default:                       return body?.message || fallback || t('errors.generic_error')
    }
}
